import { FULL_HD, HD, safetyMargins } from '@bedrock-rdk/constants';
import { getScreenSize, SCREEN_WIDTH } from '../tornado/utils/sizes';

export const BACKGROUND_COLOR = 0x99000000;

/** Used for a reading that is outside its healthy range */
export const ALERT_COLOR = 0xffff5252;

/**
 * One muted tone per metric, shared by its name, its reading and its strip, so a row reads as a unit. Kept
 * desaturated on purpose: the overlay sits on top of the app while the app is what is being looked at.
 */
export const METRIC_COLORS = {
  fps: 0xff8fb57a,
  tex: 0xffc2a86b,
  churn: 0xff7f9dbd,
  draw: 0xffa892bd,
  pool: 0xff7fb3a8,
  heap: 0xffc08f8f,
};

export const WIDTH = {
  [HD]: 232,
  [FULL_HD]: 312,
};

export const FONT_SIZE = {
  [HD]: 13,
  [FULL_HD]: 16,
};

export const PADDING = {
  [HD]: 8,
  [FULL_HD]: 10,
};

/**
 * Lightning sizes a single line text texture as `0.5 * fontSize + max(lineHeight, fontSize)`
 * (`TextTextureRenderer`: `baselineOffset + Math.max(lineHeight, fontSize)`, with the default `textBaseline` and a
 * null `offsetY`). Giving the text a `lineHeight` equal to its allocation therefore guarantees it overflows by half
 * a font size, which is what used to let a full height bar run into its own row header. The header is allocated
 * from the real formula instead, and the text is kept at `lineHeight: fontSize`.
 */
export const TEXT_HEIGHT_RATIO = 1.5;

/** Breathing room between a reading and the strip below it */
export const HEADER_GAP = {
  [HD]: 2,
  [FULL_HD]: 3,
};

export const getHeaderTextHeight = screenSize => Math.ceil(FONT_SIZE[screenSize] * TEXT_HEIGHT_RATIO);

export const getHeaderHeight = screenSize => getHeaderTextHeight(screenSize) + HEADER_GAP[screenSize];

export const GRAPH_HEIGHT = {
  [HD]: 16,
  [FULL_HD]: 22,
};

/** Gap below a strip, before the next row's name */
export const ROW_GAP = {
  [HD]: 6,
  [FULL_HD]: 8,
};

export const GRAPH_SAMPLES = 32;

export const getInnerWidth = screenSize => WIDTH[screenSize] - PADDING[screenSize] * 2;

export const getRowHeight = screenSize => getHeaderHeight(screenSize) + GRAPH_HEIGHT[screenSize] + ROW_GAP[screenSize];

/** The strips span the full inner width, so the bar width follows from the sample count */
export const getGraphBarWidth = screenSize => Math.max(Math.floor(getInnerWidth(screenSize) / GRAPH_SAMPLES), 2);

export const getInitialStyle = (screenSize = getScreenSize()) => {
  const width = WIDTH[screenSize];
  const padding = PADDING[screenSize];

  return {
    // Above every widget, so it stays readable over popups and the screensaver
    zIndex: 1000,
    x: SCREEN_WIDTH[screenSize] - width - safetyMargins.horizontal[screenSize],
    y: safetyMargins.vertical[screenSize],
    w: width,
    alpha: 0.85,
    rect: true,
    color: BACKGROUND_COLOR,
    Rows: {
      x: padding,
      y: padding,
    },
  };
};
